import { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Chip, EmptyState, LoadingState, PageContainer, type ComponentDiagnostic } from "@swot/base-components";
import { FiGitBranch, FiRefreshCw, FiShield, FiUserCheck } from "react-icons/fi";
import type { EntityRecord, ValidationFieldError } from "../../types/api.types.js";
import type { CmtsAdminAssignmentOption, CmtsAdminBlockedState, CmtsAdminIdentityPageConfig } from "../../types/adminIdentity.types.js";
import { useCmtsAssignmentMatrix } from "../../hooks/useCmtsAssignmentMatrix.js";
import { useCmtsAuditTrail } from "../../hooks/useCmtsAuditTrail.js";
import { useCmtsAdminIdentityPage } from "../../hooks/useCmtsAdminIdentityPage.js";
import { CmtsSmartDataGrid, type CmtsGridColumn, type CmtsGridRowAction } from "../data-grid/index.js";
import { CmtsEnterpriseForm } from "../forms/index.js";
import { CmtsEnterpriseModal } from "../CmtsEnterpriseModal.js";

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function readId(row: EntityRecord | null | undefined): string | undefined {
  if (!row) return undefined;
  return readString(row.id) ?? readString(row.userId) ?? readString(row.roleId) ?? readString(row.permissionId) ?? readString(row.tenantId) ?? readString(row.orgUnitId) ?? readString(row.code);
}

function readLabel(row: EntityRecord | null | undefined): string {
  if (!row) return "selected record";
  return readString(row.label) ?? readString(row.displayName) ?? readString(row.name) ?? readString(row.loginId) ?? readString(row.code) ?? readId(row) ?? "selected record";
}

export function CmtsAdminEntityStatusBadge({ row }: { readonly row: EntityRecord }) {
  const active = row.active !== false && String(row.status ?? "Active").toLowerCase() !== "inactive";
  return <Badge label={active ? "Active" : "Inactive"} tone={active ? "success" : "neutral"} />;
}

export function CmtsAdminValidationErrorPanel({ fieldErrors }: { readonly fieldErrors?: readonly ValidationFieldError[] | undefined }) {
  const rows = fieldErrors ?? [];
  if (!rows.length) return null;
  return (
    <Card title="Field validation errors" subtitle="Backend validation rejected one or more fields.">
      <CmtsSmartDataGrid
        title="Validation errors"
        rows={rows.map((row, index) => ({ id: `${row.field}-${index}`, field: row.field, code: row.code ?? "VALIDATION", message: row.message }))}
        columns={[{ id: "field", header: "Field", sortable: true, filterable: true }, { id: "code", header: "Code", sortable: true, filterable: true }, { id: "message", header: "Message", minWidth: 260 }]}
        rowKey="id"
        defaultPageSize={10}
        emptyMessage="No field validation errors."
      />
    </Card>
  );
}

export function CmtsAdminDependencyBlockedPanel({ blocked }: { readonly blocked?: CmtsAdminBlockedState | undefined }) {
  if (!blocked) return null;
  const dependencyRows = blocked.dependencies ?? [];
  return (
    <Card title={blocked.title} subtitle={blocked.message}>
      <div className="cmts-chip-row">
        <Chip label={blocked.kind} tone="warning" />
        {blocked.correlationId ? <Chip label={`Correlation: ${blocked.correlationId}`} tone="info" /> : null}
      </div>
      <CmtsAdminValidationErrorPanel fieldErrors={blocked.fieldErrors} />
      {dependencyRows.length ? (
        <CmtsSmartDataGrid
          title="Blocking dependencies"
          rows={dependencyRows}
          columns={[{ id: "entity", header: "Entity", sortable: true, filterable: true }, { id: "id", header: "ID", sortable: true, filterable: true }, { id: "code", header: "Code", sortable: true, filterable: true }, { id: "message", header: "Message", minWidth: 260 }]}
          rowKey="id"
          defaultPageSize={10}
          emptyMessage="No dependencies returned."
        />
      ) : null}
    </Card>
  );
}

export function CmtsAdminCredentialPolicyPanel({ enabled }: { readonly enabled?: boolean | undefined }) {
  return (
    <Alert title="Credential policy" tone={enabled ? "info" : "neutral"}>
      {enabled
        ? "Credential fields/actions are displayed only when backend schema or policy exposes them for this operation."
        : "Credential internals are hidden. Password creation/reset controls are not displayed unless backend policy exposes them."}
    </Alert>
  );
}

export function CmtsAdminAuditTrailPanel({ module, entityId }: { readonly module: string; readonly entityId?: string | undefined }) {
  const audit = useCmtsAuditTrail(module, entityId);
  return (
    <Card title="Audit trail" subtitle={entityId ? `Recent backend audit events for ${entityId}` : "Select a row to load entity-specific audit context."}>
      {audit.loading ? <LoadingState title="Loading audit trail" message="Requesting audit records from backend." /> : null}
      {audit.error ? <Alert title="Audit trail unavailable" tone="warning">{audit.error}</Alert> : null}
      <CmtsSmartDataGrid
        title="Admin audit events"
        rows={audit.rows}
        columns={[{ id: "createdAt", header: "Created", sortable: true }, { id: "eventType", header: "Event", sortable: true, filterable: true }, { id: "module", header: "Module", sortable: true, filterable: true }, { id: "actor", header: "Actor", sortable: true, filterable: true }, { id: "correlationId", header: "Correlation ID", filterable: true }]}
        rowKey="id"
        defaultPageSize={10}
        refreshable
        onRefresh={audit.refresh}
        emptyMessage="No audit trail records returned by backend for this selection."
      />
    </Card>
  );
}

export function CmtsAdminAssignmentDialog(props: {
  readonly title: string;
  readonly open: boolean;
  readonly options: readonly CmtsAdminAssignmentOption[];
  readonly loading?: boolean | undefined;
  readonly error?: string | undefined;
  readonly onClose: () => void;
  readonly onAssign: (id: string) => Promise<void>;
  readonly onRemove: (id: string) => Promise<void>;
}) {
  const actions: readonly CmtsGridRowAction<Record<string, unknown>>[] = [
    { id: "assign", label: "Assign", disabled: (row) => row.disabled === true || row.blocked === true, disabledReason: (row) => row.blocked === true ? "Backend marks this option as blocked." : row.disabled === true ? "Inactive option cannot be assigned." : undefined, onClick: async (row) => props.onAssign(String(row.id)) },
    { id: "remove", label: "Remove", onClick: async (row) => props.onRemove(String(row.id)) },
  ];
  return (
    <CmtsEnterpriseModal open={props.open} title={props.title} size="lg" onClose={props.onClose}>
      {props.error ? <Alert title="Assignment blocked" tone="danger">{props.error}</Alert> : null}
      <CmtsSmartDataGrid
        title={props.title}
        rows={props.options as readonly Record<string, unknown>[]}
        columns={[{ id: "code", header: "Code", sortable: true, filterable: true }, { id: "label", header: "Label", sortable: true, filterable: true }, { id: "assigned", header: "Assigned", sortable: true, filterable: true }, { id: "inherited", header: "Inherited", sortable: true, filterable: true }, { id: "blocked", header: "Blocked", sortable: true, filterable: true }]}
        rowKey="id"
        defaultPageSize={10}
        loading={props.loading}
        actions={actions}
        emptyMessage="No backend assignment options returned."
      />
    </CmtsEnterpriseModal>
  );
}

export function CmtsUserRoleAssignmentMatrix({ userId, open, onClose }: { readonly userId?: string | undefined; readonly open: boolean; readonly onClose: () => void }) {
  const matrix = useCmtsAssignmentMatrix("user-role", userId);
  return <CmtsAdminAssignmentDialog title="User-role assignment" open={open} onClose={onClose} options={matrix.options} loading={matrix.loading} error={matrix.error} onAssign={matrix.assign} onRemove={matrix.remove} />;
}

export function CmtsRolePermissionAssignmentMatrix({ roleId, open, onClose }: { readonly roleId?: string | undefined; readonly open: boolean; readonly onClose: () => void }) {
  const matrix = useCmtsAssignmentMatrix("role-permission", roleId);
  return <CmtsAdminAssignmentDialog title="Role-permission assignment" open={open} onClose={onClose} options={matrix.options} loading={matrix.loading} error={matrix.error} onAssign={matrix.assign} onRemove={matrix.remove} />;
}

export function CmtsUserOrgScopeSelector({ userId, open, onClose }: { readonly userId?: string | undefined; readonly open: boolean; readonly onClose: () => void }) {
  const matrix = useCmtsAssignmentMatrix("user-org-scope", userId);
  return <CmtsAdminAssignmentDialog title="User organization scope assignment" open={open} onClose={onClose} options={matrix.options} loading={matrix.loading} error={matrix.error} onAssign={matrix.assign} onRemove={matrix.remove} />;
}

export function CmtsAdminIdentityPage({ config, diagnostics }: { readonly config: CmtsAdminIdentityPageConfig; readonly diagnostics?: readonly ComponentDiagnostic[] | undefined }) {
  const page = useCmtsAdminIdentityPage(config);
  const [selected, setSelected] = useState<EntityRecord | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState<"roles" | "permissions" | "org" | null>(null);
  const [localMessage, setLocalMessage] = useState<string | undefined>();
  const selectedId = readId(selected);
  const showCredentialPolicy = config.supportsCredentialPolicy && page.fields.some((field) => field.name.toLowerCase().includes("password"));

  const columns = useMemo<readonly CmtsGridColumn<EntityRecord>[]>(() => [
    ...config.columns,
    { id: "statusBadge", header: "Status", accessor: (row) => row.status ?? (row.active === false ? "Inactive" : "Active"), sortable: true, filterable: true },
  ], [config.columns]);

  const actions = useMemo<readonly CmtsGridRowAction<EntityRecord>[]>(() => {
    const base: CmtsGridRowAction<EntityRecord>[] = [
      { id: "edit", label: "Edit", requiresPermission: config.permissions.update, onClick: (row) => { setSelected(row); setFormOpen(true); } },
      { id: "deactivate", label: "Deactivate", requiresPermission: config.permissions.deactivate, disabled: (row) => row.active === false || row.status === "Inactive", disabledReason: (row) => row.active === false || row.status === "Inactive" ? "Record is already inactive." : undefined, onClick: async (row) => { await page.deactivate(row); setLocalMessage(`${readLabel(row)} deactivated after backend dependency rules.`); } },
      { id: "activate", label: "Activate", requiresPermission: config.permissions.activate, hidden: (row) => !(row.active === false || row.status === "Inactive"), onClick: async (row) => { await page.activate(row); setLocalMessage(`${readLabel(row)} activated.`); } },
    ];
    if (config.entity === "users") {
      base.push({ id: "assign-roles", label: "Roles", requiresPermission: config.permissions.assign, onClick: (row) => { setSelected(row); setAssignmentOpen("roles"); } });
      base.push({ id: "assign-org", label: "Org Scope", requiresPermission: config.permissions.assign, onClick: (row) => { setSelected(row); setAssignmentOpen("org"); } });
    }
    if (config.entity === "roles") base.push({ id: "assign-permissions", label: "Permissions", requiresPermission: config.permissions.assign, onClick: (row) => { setSelected(row); setAssignmentOpen("permissions"); } });
    return base;
  }, [config, page]);

  const submit = async (payload: Record<string, unknown>) => {
    await page.save(payload, selected);
    setLocalMessage(`${readLabel({ ...selected, ...payload })} saved through backend service.`);
    setFormOpen(false);
    setSelected(null);
  };

  return (
    <PageContainer title={config.title} subtitle={config.subtitle}>
      {diagnostics?.length ? <Alert title="Admin diagnostics" tone="info">{diagnostics.map((item) => item.message).join(" | ")}</Alert> : null}
      {localMessage ? <Alert title="Admin action completed" tone="success">{localMessage}</Alert> : null}
      {page.error ? <CmtsAdminDependencyBlockedPanel blocked={page.error} /> : null}
      {page.loading ? <LoadingState title={`Loading ${config.title}`} message="Requesting backend identity records." /> : null}
      {!page.loading && !page.rows.length && !page.error ? <EmptyState title={`No ${config.title.toLowerCase()} found`} message="The backend returned an empty list for the current filters." /> : null}
      <Card title="Backend-controlled identity administration" subtitle="Routes are permission-guarded; visible menu entries remain backend /auth/my-menu driven.">
        <div className="cmts-chip-row">
          <Chip label={`Read: ${config.permissions.read}`} tone="info" />
          <Chip label={`Create: ${config.permissions.create}`} tone="info" />
          <Chip label={`Update: ${config.permissions.update}`} tone="info" />
          <Chip label={`Audit: ${config.permissions.audit}`} tone="info" />
        </div>
      </Card>
      <section className="cmts-page-action-bar">
        <Button label={`Create ${config.title.replace(/s$/, "")}`} leftIcon={<FiUserCheck />} disabled={page.saving} onClick={() => { setSelected(null); setFormOpen(true); }} />
        <Button label="Refresh" variant="secondary" leftIcon={<FiRefreshCw />} disabled={page.loading || page.saving} onClick={() => void page.refresh()} />
      </section>
      <CmtsSmartDataGrid
        title={config.title}
        rows={page.rows}
        columns={columns}
        rowKey={config.rowKey}
        defaultPageSize={25}
        loading={page.loading}
        actions={actions}
        refreshable
        onRefresh={page.refresh}
        emptyMessage={`No ${config.title.toLowerCase()} returned by backend.`}
      />
      <CmtsAdminAuditTrailPanel module={config.auditModule} entityId={selectedId} />
      {config.supportsCredentialPolicy ? <CmtsAdminCredentialPolicyPanel enabled={Boolean(showCredentialPolicy)} /> : null}
      <CmtsEnterpriseModal open={formOpen} title={selected ? `Edit ${config.title}` : `Create ${config.title}`} subtitle="Backend schema, validation and dependency rules govern this form." size="lg" onClose={() => { setFormOpen(false); setSelected(null); }}>
        <CmtsEnterpriseForm fields={page.fields} initialValues={selected ?? undefined} busy={page.saving} error={page.error?.message} submitLabel={selected ? "Save changes" : "Create"} onCancel={() => { setFormOpen(false); setSelected(null); }} onSubmit={submit}>
          <Alert title="Backend validation" tone="info"><FiShield /> Save uses the canonical identity service and backend validation when the endpoint is available.</Alert>
        </CmtsEnterpriseForm>
      </CmtsEnterpriseModal>
      <CmtsUserRoleAssignmentMatrix userId={selectedId} open={assignmentOpen === "roles"} onClose={() => setAssignmentOpen(null)} />
      <CmtsRolePermissionAssignmentMatrix roleId={selectedId} open={assignmentOpen === "permissions"} onClose={() => setAssignmentOpen(null)} />
      <CmtsUserOrgScopeSelector userId={selectedId} open={assignmentOpen === "org"} onClose={() => setAssignmentOpen(null)} />
    </PageContainer>
  );
}
